import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Typography, LinearProgress, Alert, IconButton, Tooltip, useTheme, useMediaQuery, Fade } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight, Help } from "@mui/icons-material";
import MultiStepCardViewer from "./MultiCardStepViewer";
import ReviewMode from "./ReviewMode/ReviewMode";
import AlreadyKnowButton from "./AlreadyKnowButton";
import LearningPathBreadcrumb from "./LearningPathBreadcrumb";
import CardNavigationHelpGuide from "./CardNavigationHelpGuide";
import api from "../../api";
import { jwtDecode } from "jwt-decode";
import CardPreprocessor from '../../utils/CardPreprocessor';
import languageLearningApi from "../language_learning/middleware/languageLearningApi";
import { useNavigation } from "../../context/NavigationContext"; // Import the navigation context
import CourseIdToWebkitLanguage from '../../static_mappings/CourseIdToWebkitLanguage.json';
import CourseletSummary from "../language_learning/user_console/CourseletSummary";
// This constant is now only used for non-course mode
const BATCH_SIZE = 1000;
// Transition duration in milliseconds
const TRANSITION_DURATION = 200;

const MultiStepCardLearningManager = ({
  token,
  courseMode = true,
  courseId = null // Keep for backward compatibility
}) => {
  let deckId;

  const navigate = useNavigate();
  const { currentCourse } = useNavigation(); // Get current course from context
  console.log('Debug: currentCourse ', currentCourse);
  if (courseMode) {
    if (!currentCourse) {
      navigate('/languages')
    }
    else {
      deckId = currentCourse.deckId;
    }
  } else {
    console.log('Debug: courseMode is false');
  }
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Help guide state
  const [showHelpGuide, setShowHelpGuide] = useState(false);

  // Transition states
  const [fadeIn, setFadeIn] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Get the stored course from localStorage
  const getStoredCourse = () => {
    const storedCourse = localStorage.getItem('selectedCourse');
    return storedCourse ? JSON.parse(storedCourse) : null;
  };

  // Use courseId from props if provided, otherwise use from context or localStorage
  const [effectiveCourseId, setEffectiveCourseId] = useState(null);
  const [activeCourse, setActiveCourse] = useState(null);

  useEffect(() => {
    // First check for courseId prop
    if (courseId) {
      setEffectiveCourseId(courseId);
    }
    // Then check for currentCourse from context
    else if (currentCourse && currentCourse._id) {
      setEffectiveCourseId(currentCourse._id);
      setActiveCourse(currentCourse);
      // Store in localStorage for persistence
      localStorage.setItem('selectedCourse', JSON.stringify(currentCourse));
    }
    // Finally check localStorage
    else {
      const storedCourse = getStoredCourse();
      if (storedCourse && storedCourse._id) {
        setEffectiveCourseId(storedCourse._id);
        setActiveCourse(storedCourse);
      }
    }
  }, [courseId, currentCourse]);

  const [deck, setDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardBatches, setCardBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [course, setCourse] = useState(null);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [learnMode, setLearnMode] = useState(false);
  const [rawCards, setRawCards] = useState([]);
  const [rawCardBatches, setRawCardBatches] = useState([]);
  const [processedReviewCards, setProcessedReviewCards] = useState([]);
  const [burntCardIds, setBurntCardIds] = useState(new Set());
  const [currentCardMetadata, setCurrentCardMetadata] = useState(null);
  const [nextBatchToLoad, setNextBatchToLoad] = useState(null);
  const [error, setError] = useState(null); // New error state for handling missing course

  // New state for courselet completion
  const [showCourseletSummary, setShowCourseletSummary] = useState(false);
  const [completedCourseletInfo, setCompletedCourseletInfo] = useState(null);

  const cardViewerRef = useRef(null);
  const learningSubmitted = useRef(false);

  // Decode token to obtain userId
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.user._id);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [token]);

  useEffect(() => {
    const handleHelpKeyToggle = (event) => {
      // Only toggle if not in input field or review mode
      if (event.key.toLowerCase() === 'h' &&
        !learnMode &&
        !['input', 'textarea', 'select'].includes(document.activeElement.tagName.toLowerCase())) {
        setShowHelpGuide(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleHelpKeyToggle);
    return () => window.removeEventListener('keydown', handleHelpKeyToggle);
  }, [learnMode]);

  // When component mounts, ensure we start with fadeIn true
  useEffect(() => {
    setFadeIn(true);
  }, []);

  // Fetch deck and cards from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!courseMode) {
          // Standard deck learning flow - fixed batch size
          const deckResponse = await api.get(`/decks/${deckId}`);
          setDeck(deckResponse.data.deck);

          const cardsResponse = await api.get(`/decks/${deckId}/learn`);
          // Store raw cards, don't process yet
          setRawCards(cardsResponse.data.newCards || []);
          setCards(cardsResponse.data.newCards || []);
        } else {
          // Course learning flow - dynamic batching based on sentences
          if (!effectiveCourseId) {
            setError("No course selected. Please select a course first.");
            setLoading(false);
            return;
          }

          const courseResponse = await languageLearningApi.get(`/courses/${effectiveCourseId}`);
          setCourse(courseResponse.data);

          // For course mode, we still need deck info for the learning completion
          const deckResponse = await api.get(`/decks/${deckId}`);
          setDeck(deckResponse.data.deck);

          await fetchLearningQueue();
        }
      } catch (error) {
        console.error("Error fetching learning data:", error);
        setError("Failed to load learning data. Please try again later.");
        setCards([]);
        setCardBatches([]);
      } finally {
        setLoading(false);
      }
    };

    if (effectiveCourseId || !courseMode) {
      fetchData();
    }
  }, [deckId, courseMode, effectiveCourseId, token]);

  // Function to fetch learning queue
  const fetchLearningQueue = async () => {
    try {
      const decoded = jwtDecode(token);

      // Get course language from activeCourse or from currentCourse
      const languageToUse = activeCourse?.language || (currentCourse && currentCourse.language);

      if (!languageToUse) {
        console.error("No language found for the course");
        setError("Course language information is missing. Please select a course again.");
        return;
      }

      const response = await languageLearningApi.get(
        `/learning-queue?userId=${decoded.user._id}&courseId=${effectiveCourseId}&language=${languageToUse}`
      );

      // Process titles if needed
      const titlesProcessed = response.data.learningQueue.map(card => ({
        ...card,
        title: card.title?.split(':')[0] || card.title
      }));

      // Group cards without preprocessing
      const batches = [];
      let currentBatch = [];
      let currentSentenceId = null;

      titlesProcessed.forEach(card => {
        const isSentence = card.metadata && card.metadata.sentenceId;

        if (isSentence) {
          if (currentBatch.length > 0) {
            batches.push([...currentBatch]);
          }

          currentBatch = [card];
          currentSentenceId = card.metadata.sentenceId;
        } else {
          currentBatch.push(card);
        }
      });

      if (currentBatch.length > 0) {
        batches.push(currentBatch);
      }

      // Store batches
      setRawCardBatches(batches);
      setCardBatches(batches);

      // Flatten the batches for display in the UI
      const allCards = batches.flat();
      setRawCards(allCards);
      setCards(allCards);

    } catch (error) {
      console.error("Error fetching learning queue:", error);
      setError("Failed to load learning queue. Please try again later.");
    }
  };

  // Filter current batch to exclude burnt cards
  const filteredCurrentBatch = React.useMemo(() => {
    if (courseMode) {
      const batch = cardBatches[currentBatchIndex] || [];
      return batch.filter(card => !burntCardIds.has(card.cardId || card._id));
    } else {
      const batch = cards.slice(
        currentBatchIndex * BATCH_SIZE,
        (currentBatchIndex + 1) * BATCH_SIZE
      );
      return batch.filter(card => !burntCardIds.has(card.cardId || card._id));
    }
  }, [courseMode, cardBatches, currentBatchIndex, cards, burntCardIds]);

  // Process cards and prepare for review
  const prepareCardsForReview = useCallback((cardsToProcess) => {
    console.log('Invoking prepareCardsForReview with   ', cardsToProcess);
    // Process the current batch
    const processedCards = CardPreprocessor.processCards(cardsToProcess);

    // Flatten cards with variants using a better approach to avoid duplicates
    const flattenedCards = [];
    const seenVariantIds = new Set(); // Track which variants we've already added

    processedCards.forEach(card => {
      // Skip the main card if it's a variant container or has question type "container"
      const skipMainCard = card.isVariantContainer || card.questionType === "container";

      // Only add the original card if it's not a variant container
      if (!skipMainCard) {
        const originalId = `${card._id || card.cardId}-original`;
        if (!seenVariantIds.has(originalId)) {
          seenVariantIds.add(originalId);
          flattenedCards.push({
            ...card,
            variantId: originalId,
            originalCard: card._id || card.cardId
          });
        }
      }

      // Then add all variants
      if (card.variants && card.variants.length > 0) {
        card.variants.forEach((variant, index) => {
          const variantId = `${card._id || card.cardId}-variant-${index}`;
          if (!seenVariantIds.has(variantId)) {
            seenVariantIds.add(variantId);
            flattenedCards.push({
              ...variant,
              _id: card._id || card.cardId,
              variantId: variantId,
              title: card.title,
              answer: card.answer,
              metadata: card.metadata,
              steps: card.steps,
              audioData: card.audioData,
              audio: card.audio,
              originalCard: card._id || card.cardId,
              sentenceId: card.metadata?.sentenceId
            });
          }
        });
      }
    });

    return flattenedCards;
  }, []);
  // Navigate to the previous card in the current batch (if exists)
  const handlePrevCard = useCallback(() => {
    if (currentCardIndex > 0 && !isTransitioning) {
      // Start transition
      setIsTransitioning(true);
      setFadeIn(false);

      // Wait for fade-out to complete
      setTimeout(() => {
        setCurrentCardIndex((prev) => prev - 1);
        // Set the current card metadata
        const prevCard = filteredCurrentBatch[currentCardIndex - 1];
        setCurrentCardMetadata(prevCard?.metadata || null);
        if (cardViewerRef.current) {
          cardViewerRef.current.setStep(0);
        }

        // Start fade in
        setFadeIn(true);

        // End transition after fade-in is complete
        setTimeout(() => {
          setIsTransitioning(false);
        }, TRANSITION_DURATION);
      }, TRANSITION_DURATION);
    }
  }, [currentCardIndex, filteredCurrentBatch, isTransitioning]);

  // Find the next batch with unburnt cards
  const findNextBatchWithUnburntCards = useCallback((startIndex) => {
    for (let i = startIndex; i < cardBatches.length; i++) {
      const hasUnburntCard = cardBatches[i].some(card =>
        !burntCardIds.has(card.cardId || card._id)
      );

      if (hasUnburntCard) {
        return i;
      }
    }
    return -1;
  }, [cardBatches, burntCardIds]);

  // Prepare for review mode
  const prepareAndEnterReviewMode = useCallback(() => {
    console.log('Invoking prepareAndEnterReviewMode ');
    let cardsForReview;

    if (courseMode) {
      // For course mode, only review the current batch
      const currentBatch = cardBatches[currentBatchIndex] || [];
      const filteredBatch = currentBatch.filter(card =>
        !burntCardIds.has(card.cardId || card._id)
      );

      // Skip review if all cards are burnt (user burned entire sentence with "I already know this")
      if (filteredBatch.length === 0) {
        console.log('All cards in batch are burnt, skipping review and moving to next batch');
        // Handle empty batch case - check if we have a next batch to load
        if (nextBatchToLoad !== null) {
          setCurrentBatchIndex(nextBatchToLoad);
          setCurrentCardIndex(0);
          setNextBatchToLoad(null);
          return;
        } else {
          // No more batches with unburnt cards
          navigate(courseMode ? "/course-learn" : "/dashboard");
          return;
        }
      }

      cardsForReview = prepareCardsForReview(filteredBatch);
    } else {
      // For standard mode with fixed batch size
      const batchStart = currentBatchIndex * BATCH_SIZE;
      const batchEnd = (currentBatchIndex + 1) * BATCH_SIZE;
      const currentRawBatch = rawCards.slice(batchStart, batchEnd);
      const filteredBatch = currentRawBatch.filter(card =>
        !burntCardIds.has(card.cardId || card._id)
      );

      if (filteredBatch.length === 0) {
        // All cards in batch are burnt, move to next batch or finish
        const nextBatchStart = (currentBatchIndex + 1) * BATCH_SIZE;
        if (nextBatchStart < cards.length) {
          setCurrentBatchIndex((prev) => prev + 1);
          setCurrentCardIndex(0);
          return;
        } else {
          navigate("/dashboard");
          return;
        }
      }

      cardsForReview = prepareCardsForReview(filteredBatch);
    }

    // Shuffle the cards
    cardsForReview = CardPreprocessor.shuffleArray(cardsForReview);

    // Store the processed cards for review mode
    setProcessedReviewCards(cardsForReview);

    // Enter learn mode with processed cards
    setLearnMode(true);
  }, [courseMode, cardBatches, currentBatchIndex, rawCards, cards, burntCardIds, navigate, prepareCardsForReview, nextBatchToLoad]);

  // Navigate to the next card or prepare for review
  const handleNextCard = useCallback(() => {
    // Prevent multiple navigation during transition
    if (isTransitioning) return;

    // Check if there are more cards in the current batch
    if (currentCardIndex < filteredCurrentBatch.length - 1) {
      // Start transition
      setIsTransitioning(true);
      setFadeIn(false);

      // Wait for fade-out to complete
      setTimeout(() => {
        // Move to next card
        setCurrentCardIndex((prev) => prev + 1);
        // Set the current card metadata
        const nextCard = filteredCurrentBatch[currentCardIndex + 1];
        setCurrentCardMetadata(nextCard?.metadata || null);
        if (cardViewerRef.current) {
          cardViewerRef.current.setStep(0);
        }

        // Start fade in
        setFadeIn(true);

        // End transition after fade-in is complete
        setTimeout(() => {
          setIsTransitioning(false);
        }, TRANSITION_DURATION);
      }, TRANSITION_DURATION);
    } else {
      // At the last card in the current batch

      // If we're in course mode, check if there are more batches with unburnt cards
      if (courseMode) {
        console.log('CourseMode behavior');
        let nextBatchWithUnburntCards = findNextBatchWithUnburntCards(currentBatchIndex + 1);

        if (nextBatchWithUnburntCards >= 0) {
          // Store the next batch index to load after review is complete
          setNextBatchToLoad(nextBatchWithUnburntCards);
          console.log('Found next batch:', nextBatchWithUnburntCards);
        }
      }

      // Always prepare for review mode when reaching the end of a batch
      console.log('ReviewMode - awaken');
      prepareAndEnterReviewMode();
    }
  }, [currentCardIndex, filteredCurrentBatch, courseMode, currentBatchIndex, findNextBatchWithUnburntCards, prepareAndEnterReviewMode, isTransitioning]);

  // Handle when a card is burnt
  const handleCardBurnt = useCallback((burntIds) => {
    // Add burnt card IDs to our set
    const newBurntCardIds = new Set(burntCardIds);
    burntIds.forEach(id => newBurntCardIds.add(id));
    setBurntCardIds(newBurntCardIds);

    // Move to next card
    handleNextCard();
  }, [burntCardIds, handleNextCard]);

  // Global keydown handler for card-level navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!cardViewerRef.current || learnMode || isTransitioning) return;

      const { currentStep, totalSteps } = cardViewerRef.current.getStatus();

      // Card to card navigation with shift key or at card boundaries
      if ((currentStep === 0 || event.shiftKey) && event.key === "ArrowLeft") {
        event.preventDefault();
        handlePrevCard();
      } else if ((event.shiftKey || currentStep === totalSteps - 1) && event.key === "ArrowRight") {
        event.preventDefault();
        handleNextCard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentCardIndex, filteredCurrentBatch, learnMode, handleNextCard, handlePrevCard, isTransitioning]);

  // After setting the current card index in any way, update the metadata
  useEffect(() => {
    if (filteredCurrentBatch && filteredCurrentBatch.length > 0) {
      const card = filteredCurrentBatch[currentCardIndex];
      setCurrentCardMetadata(card?.metadata || null);
    }
  }, [currentCardIndex, filteredCurrentBatch]);

  // Handler when review is complete for the current batch
  const handleReviewComplete = async (reviewResults) => {
    if (learningSubmitted.current) {
      return;
    }
    learningSubmitted.current = true;

    // Check if this is the end of a courselet (no more batches with unburnt cards)
    if (courseMode) {
      const nextBatchIndex = findNextBatchWithUnburntCards(currentBatchIndex + 1);
      
      if (nextBatchIndex < 0 && nextBatchToLoad === null) {
        // No more batches - courselet is complete!
        setCompletedCourseletInfo({
          courseletTitle: course?.course?.title || deck?.name || "this courselet",
          courseletId: effectiveCourseId,
          courseId: effectiveCourseId,
          sentenceCount: cardBatches.length
        });
        setShowCourseletSummary(true);
        return;
      }
    }

    // Move to next batch or finish
    if (courseMode) {
      // Check if we have a next batch waiting to be loaded
      if (nextBatchToLoad !== null) {
        console.log('Moving to next batch after review:', nextBatchToLoad);
        setCurrentBatchIndex(nextBatchToLoad);
        setCurrentCardIndex(0);
        setLearnMode(false);
        setProcessedReviewCards([]);
        setNextBatchToLoad(null);
        learningSubmitted.current = false; // Reset for next batch
      } else {
        // Check if there are any other batches with unburnt cards
        const nextBatchIndex = findNextBatchWithUnburntCards(currentBatchIndex + 1);

        if (nextBatchIndex >= 0) {
          setCurrentBatchIndex(nextBatchIndex);
          setCurrentCardIndex(0);
          setLearnMode(false);
          setProcessedReviewCards([]);
          learningSubmitted.current = false; // Reset for next batch
        } else {
          // No more unburnt cards in any batch
          navigate(courseMode ? "/course-learn" : "/dashboard");
        }
      }
    } else {
      // For standard mode with fixed batch size
      const nextBatchStart = (currentBatchIndex + 1) * BATCH_SIZE;
      if (nextBatchStart < cards.length) {
        setCurrentBatchIndex((prev) => prev + 1);
        setCurrentCardIndex(0);
        setLearnMode(false);
        setProcessedReviewCards([]);
        learningSubmitted.current = false; // Reset for next batch
      } else {
        // All batches completed, redirect to dashboard
        navigate(courseMode ? "/course-learn" : "/dashboard");
      }
    }
  };

  // Error state - display when no course is selected in course mode
  if (error) {
    return (
      <Box sx={{ width: "85%", mt: 3, mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/languages")}
          >
            Go to Language Selection
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: "85%", mt: 3, mx: "auto", background: 'rgb(247, 244, 239)' }}>
        <LinearProgress />
        <Typography sx={{ m: 1.5, textAlign: "center", fontSize: "0.85rem" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!deck || filteredCurrentBatch.length === 0) {
    return (
      <Box sx={{ m: 3, textAlign: "center", width: "85%", mx: "auto" }}>
        <Typography variant="h5" sx={{ fontSize: "1.2rem" }}>No cards available</Typography>
        <Typography color="text.secondary" sx={{ fontSize: "0.85rem" }}>
          There are no learning items to display.
        </Typography>
      </Box>
    );
  }

  // When in learn mode, render the ReviewMode component with the already processed cards
  if (learnMode) {
    console.log('Invoking review mode with processedReviewCards ', processedReviewCards);
    return (
      <Box sx={{ width: "85%", mx: "auto" }}>
        <ReviewMode
          token={token}
          initialCards={processedReviewCards}
          deck={deck}
          onReviewComplete={handleReviewComplete}
          courseMode={courseMode}
          courseId={effectiveCourseId}
          userId={userId}
        />
      </Box>
    );
  }

  const currentCard = filteredCurrentBatch[currentCardIndex];

  // Show courselet summary when complete
  if (showCourseletSummary && completedCourseletInfo) {
    return (
      <CourseletSummary {...completedCourseletInfo} />
    );
  }

  return (
    <Box sx={{
      position: "relative",
      mx: "auto",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Help Guide Component */}
      <Box sx={{
        position: "fixed",
        top: { xs: "56px", sm: "128px" }, // Adjust based on navbar height
        right: 0,
        zIndex: 1000
      }} />
      <CardNavigationHelpGuide
        open={showHelpGuide}
        onClose={() => setShowHelpGuide(false)}
      />

      {/* Add the LearningPathBreadcrumb component here */}
      {courseMode && currentCardMetadata && (
        <LearningPathBreadcrumb
          metadata={currentCardMetadata}
          courseTitle={course?.course.title || deck?.name}
        />
      )}

      <Box sx={{
        flex: 0,
        position: "relative",
        overflowX: "hidden",
        maxWidth: "100%"
      }}>
        {/* IMPROVED BUTTON CONTAINER: Responsive with proper alignment */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 10,
          zIndex: 5,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',  // Column on mobile
          alignItems: isMobile ? 'flex-end' : 'center', // Right-aligned on mobile
          gap: isMobile ? 1.5 : 2,  // Smaller gap on mobile
          width: 'auto',
          maxWidth: isMobile ? '160px' : 'auto'  // Limit width on mobile
        }}>
          {/* "I already know this" button when in course mode */}
          {courseMode && currentCard && (
            <AlreadyKnowButton
              cardId={currentCard.cardId || currentCard._id}
              deckId={deckId}
              sentenceId={currentCard.metadata?.sentenceId}
              userId={userId}
              courseId={effectiveCourseId}
              onBurnComplete={handleCardBurnt}
            />
          )}

          {/* Help icon button - now enhanced with better styling */}
          <IconButton
            onClick={() => setShowHelpGuide(true)}
            sx={{
              bgcolor: 'rgba(69, 39, 160, 0.1)',  // Improved purple color to match help dialog
              color: showHelpGuide ? '#9C27B0' : '#4527a0',  // Change color when active
              border: '1px solid rgba(69, 39, 160, 0.3)',
              boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
              padding: isMobile ? '8px' : '10px',  // Smaller on mobile
              '&:hover': {
                bgcolor: showHelpGuide ? 'rgba(156, 39, 176, 0.2)' : 'rgba(69, 39, 160, 0.2)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                transition: 'all 0.2s ease',
              },
              transition: 'all 0.2s ease',
              minWidth: isMobile ? '42px' : '48px',
              width: isMobile ? '42px' : '48px',
              height: isMobile ? '42px' : '48px'
            }}
            aria-pressed={showHelpGuide}
          >
            <Tooltip title="Help Guide (Press H)">
              <Help fontSize={isMobile ? "small" : "medium"} />
            </Tooltip>
          </IconButton>
        </Box>

        <Fade in={fadeIn} timeout={TRANSITION_DURATION}>
          <Box>
            <MultiStepCardViewer
              key={`card-${currentCard._id || currentCardIndex}`}
              ref={cardViewerRef}
              cardData={currentCard}
              language={CourseIdToWebkitLanguage[effectiveCourseId]}
              onPrevCard={handlePrevCard}
              onNextCard={handleNextCard}
            />
          </Box>
        </Fade>

        {/* Navigation arrows - keeping these intact as requested */}
        {currentCardIndex > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: 12,
              transform: "translateY(-50%)",
            }}
          >
            <Tooltip title="Previous Card (Shift + ←)">
              <IconButton
                onClick={handlePrevCard}
                disabled={isTransitioning}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(216, 241, 247, 1)',
                  }
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 12,
            transform: "translateY(-50%)",
          }}
        >
          <Tooltip title="Next Card (Shift + →)">
            <IconButton
              onClick={handleNextCard}
              disabled={isTransitioning}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(216, 241, 247, 1)',
                }
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Mini card navigator pushed to bottom - reduced size */}
      {filteredCurrentBatch.length < 10 && !learnMode && (
        <Box
          sx={{
            padding: 1.5,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "auto",
            marginBottom: 1.5,
            position: "relative",
            background: 'rgb(216, 241, 247)',
            borderRadius: "4px"
          }}
        >
          <Box sx={{ display: "flex", gap: 0.75 }}>
            {filteredCurrentBatch.map((card, idx) => (
              <Tooltip
                key={card._id || idx}
                title={`Go to card: ${card.title || "Card " + (idx + 1)}`}
              >
                <Box
                  onClick={() => {
                    if (!isTransitioning && idx !== currentCardIndex) {
                      // Start transition
                      setIsTransitioning(true);
                      setFadeIn(false);

                      // Wait for fade-out to complete
                      setTimeout(() => {
                        setCurrentCardIndex(idx);
                        if (cardViewerRef.current) {
                          cardViewerRef.current.setStep(0);
                        }

                        // Start fade in
                        setFadeIn(true);

                        // End transition after fade-in is complete
                        setTimeout(() => {
                          setIsTransitioning(false);
                        }, TRANSITION_DURATION);
                      }, TRANSITION_DURATION);
                    }
                  }}
                  sx={{
                    width: 34,
                    height: 34,
                    backgroundColor: idx === currentCardIndex
                      ? "rgba(76, 175, 80, 0.9)"
                      : "rgba(33, 150, 243, 0.8)",
                    color: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: isTransitioning ? "default" : "pointer",
                    borderRadius: 1,
                    fontSize: "0.64rem",
                    fontWeight: "bold",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: isTransitioning ? "none" : "scale(1.1)",
                      boxShadow: isTransitioning ? "none" : "0 2px 5px rgba(0,0,0,0.2)"
                    },
                    opacity: isTransitioning ? 0.7 : 1
                  }}
                >
                  {card.title ? card.title.slice(0, 3).toUpperCase() : (idx + 1)}
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Typography sx={{ marginTop: 1, marginBottom: 0.5, fontSize: '0.85rem' }}>
            Card {currentCardIndex + 1} of {filteredCurrentBatch.length} in this batch
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MultiStepCardLearningManager;